// What the Pads screen can do to a pad, decided without React.
//
// The pads are Live's Session View at a thumb's size, and editing them follows
// the two touch grids people already know: GarageBand's Live Loops (an Edit
// switch, after which a tap selects a cell instead of playing it) and Ableton
// Note / Push (hold a pad to act on it). Either way the selection is one of
// three things — a pad, an empty slot, or a scene row — and every operation
// below is decided against the room it is about to edit.
//
// Two kinds of pad, and the difference matters:
//
// - A SCENE pad is a real Session View clip. It is edited in place, exactly
//   as Live would edit it.
// - A TIMELINE pad is a ghost (lib/ghost-clips.ts): the loop the grid offers
//   from the arrangement, which Live knows nothing about. Editing one in place
//   would mean editing a placement on the timeline from a screen that never
//   showed it, so it is KEPT first — copied into a scene slot on its own
//   lane, braced the way the ghost played — and the copy is what gets edited.
//   The arrangement is never touched behind the producer's back.

import type { CanonicalClipState, CanonicalRoom } from "@shared/sync/state.js";
import { MAX_SCENES } from "@shared/sync/state.js";
import { findClip } from "@web-shared/lib/edits";

/** The id prefix ghost pads carry (lib/ghost-clips.ts `ghostOf`). */
const GHOST_PREFIX = "ghost:";

/** What the pads screen has selected. */
export type PadSelection =
  | { kind: "pad"; clipId: string; laneId: string; row: number }
  | { kind: "slot"; laneId: string; row: number }
  | { kind: "scene"; row: number };

/** The timeline clip a ghost pad stands for, or null for a real pad. */
export function ghostSource(padId: string): string | null {
  return padId.startsWith(GHOST_PREFIX) ? padId.slice(GHOST_PREFIX.length) : null;
}

/** The room's clip behind a pad — the slot clip itself, or a ghost's timeline
 * source. Null once the room no longer has it. */
export function padClip(room: CanonicalRoom | null, padId: string): CanonicalClipState | null {
  if (!room) return null;
  return findClip(room, ghostSource(padId) ?? padId);
}

function occupied(room: CanonicalRoom, trackId: string): Set<number> {
  const rows = new Set<number>();
  for (const c of room.clips) if (c.trackId === trackId && c.slot !== undefined) rows.add(c.slot);
  return rows;
}

/** Whether a lane's slot holds a clip. */
export function slotTaken(room: CanonicalRoom, trackId: string, row: number): boolean {
  return room.clips.some((c) => c.trackId === trackId && c.slot === row);
}

/**
 * Where a pad's Duplicate lands: the first empty slot BELOW it on its own
 * lane, as Live's Duplicate does. Null when the lane is full to the cap.
 */
export function duplicateRow(room: CanonicalRoom, trackId: string, row: number): number | null {
  const taken = occupied(room, trackId);
  for (let r = row + 1; r < MAX_SCENES; r++) if (!taken.has(r)) return r;
  return null;
}

/**
 * Where a timeline pad is kept: the first empty slot on its lane among the
 * Set's own scenes, so it joins a row that already exists; past them, a new
 * row of its own. `sceneCount` is the Set's scene count WITHOUT the ghost
 * rows (`fromRoom`'s, not `withGhostSlots`'s).
 */
export function keepRow(room: CanonicalRoom, trackId: string, sceneCount: number): number | null {
  const taken = occupied(room, trackId);
  for (let r = 0; r < sceneCount && r < MAX_SCENES; r++) if (!taken.has(r)) return r;
  for (let r = Math.max(0, sceneCount); r < MAX_SCENES; r++) if (!taken.has(r)) return r;
  return null;
}

/** A slot can take a clip when it is empty and the lane is the clip's kind. */
export function slotAccepts(room: CanonicalRoom, clip: CanonicalClipState, trackId: string, row: number): boolean {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track) return false;
  if ((track.trackKind ?? "audio") !== (clip.clipKind ?? "audio")) return false;
  return !slotTaken(room, trackId, row);
}

/**
 * A selection after the room has moved on: a pad the room deleted (a peer, an
 * undo) is gone, a slot something now fills becomes that pad, and a slot or
 * scene stays as long as its lane does.
 */
export function reconcileSelection(room: CanonicalRoom | null, sel: PadSelection | null): PadSelection | null {
  if (!room || !sel) return sel;
  if (sel.kind === "scene") return sel;
  if (!room.tracks.some((t) => t.id === sel.laneId)) return null;
  if (sel.kind === "pad") return padClip(room, sel.clipId) ? sel : null;
  const filled = room.clips.find((c) => c.trackId === sel.laneId && c.slot === sel.row);
  return filled ? { kind: "pad", clipId: filled.id, laneId: sel.laneId, row: sel.row } : sel;
}

/** Same selection, compared by value — a re-tap on what is already selected
 * clears it. */
export function sameSelection(a: PadSelection | null, b: PadSelection | null): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === "scene") return a.row === b.row;
  if (a.kind === "pad") return b.kind === "pad" && a.clipId === b.clipId;
  return b.kind === "slot" && a.laneId === b.laneId && a.row === b.row;
}
